/**
 * Feature Comparison V2 Component
 *
 * Redesigned feature comparison table with:
 * - Slider/toggle controls (sticky, synced with main pricing table)
 * - Sticky column headers with pricing
 * - Plan columns gray out when slider exceeds their threshold
 * - White background for readability
 *
 * @module components/billing/FeatureComparisonV2
 */

import { Fragment } from 'react';
import { Info, Check, Minus, Repeat, Zap, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, Button } from '@/components/ui';
import {
  type PlanId,
  type BillingInterval,
  FEATURE_CATEGORY_LABELS,
  getOrderedCategories,
  getFeaturesByCategory,
  getOrderedPlanTiers,
  SLIDER_STOPS,
  isPlanDisabledAtRequestCount,
  getBucketForRequestCount,
  formatRequestCount,
  formatPrice,
  calculateIntroPrice,
} from '@hummingbird/pricing';

// =============================================================================
// Types
// =============================================================================

export interface FeatureComparisonV2Props {
  /** Current user's plan (to highlight) */
  currentPlanId?: PlanId;
  /** Current user's bucket max (for downgrade detection) */
  currentBucketMax?: number;
  /** Whether user is eligible for intro pricing */
  isEligibleForIntro?: boolean;
  /** Callback when user selects a plan */
  onSelectPlan?: (planId: PlanId, requests: number, interval: BillingInterval) => void;
  /** Whether an action is in progress */
  isLoading?: boolean;
  /** Loading plan ID */
  loadingPlanId?: PlanId;
  /** Controlled billing interval */
  interval: BillingInterval;
  /** Callback when interval changes */
  onIntervalChange: (interval: BillingInterval) => void;
  /** Controlled slider index */
  sliderIndex: number;
  /** Callback when slider index changes */
  onSliderIndexChange: (index: number) => void;
  /** Additional class names */
  className?: string;
}

// =============================================================================
// Confirmation Benefits
// =============================================================================

const CONFIRMATION_BENEFITS = [
  {
    icon: Repeat,
    text: 'Cancel anytime (no lock-in)',
  },
  {
    icon: Zap,
    text: 'Same API, higher limits',
  },
  {
    icon: DollarSign,
    text: 'Pro-rated upgrades, no surprises',
  },
];

// =============================================================================
// Plan Header Component
// =============================================================================

interface PlanHeaderProps {
  planId: PlanId;
  planName: string;
  interval: BillingInterval;
  selectedRequests: number;
  isCurrentPlan: boolean;
  isDisabled: boolean;
  isEligibleForIntro: boolean;
  isDowngrade: boolean;
  onSelect: () => void;
  isLoading: boolean;
}

function PlanHeader({
  planId,
  planName,
  interval,
  selectedRequests,
  isCurrentPlan,
  isDisabled,
  isEligibleForIntro,
  isDowngrade,
  onSelect,
  isLoading,
}: PlanHeaderProps) {
  const isFree = planId === 'free';
  const isEnterprise = planId === 'enterprise';
  const bucket = getBucketForRequestCount(selectedRequests, planId);

  // Calculate pricing
  const getPricing = () => {
    if (isFree) {
      return { main: '$0', sub: '/month', topLine: '' };
    }
    if (isEnterprise) {
      return { main: '$99.99', sub: '/mo', topLine: 'starts at' };
    }
    if (!bucket) {
      return { main: '—', sub: '', topLine: '' };
    }

    const monthlyPrice = bucket.monthlyPriceCents;
    const annualPrice = bucket.annualPriceCents;

    if (interval === 'annual') {
      const monthlyEquivalent = Math.round(annualPrice / 12);
      if (isEligibleForIntro) {
        const introMonthly = calculateIntroPrice(monthlyEquivalent);
        return {
          main: formatPrice(introMonthly),
          sub: '/mo',
          topLine: `normally ${formatPrice(monthlyEquivalent)}/mo`,
          topLineStrikethrough: true,
        };
      }
      // For existing subscribers, show monthly price crossed out
      return {
        main: formatPrice(monthlyEquivalent),
        sub: '/mo',
        topLine: `normally ${formatPrice(monthlyPrice)}/mo`,
        topLineStrikethrough: true,
      };
    }

    // Monthly - no intro discount (coupon only applies to annual)
    return { main: formatPrice(monthlyPrice), sub: '/mo', topLine: '' };
  };

  const pricing = getPricing();

  const getButtonText = () => {
    if (isCurrentPlan) return 'Current Plan';
    if (isFree) return 'Start for Free';
    if (isEnterprise) return 'Contact Sales';
    if (isDisabled) return 'Not Available';
    if (isDowngrade) return 'Downgrade';
    return 'Upgrade Now';
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center py-3 px-2',
        isDisabled && 'opacity-50'
      )}
    >
      {/* Current badge - always reserve space */}
      <div className="h-5 flex items-center justify-center mb-1">
        {isCurrentPlan && (
          <span className="text-[10px] bg-brand-600 text-white px-1.5 py-0.5 rounded-full">
            Current
          </span>
        )}
      </div>

      {/* Plan name */}
      <span
        className={cn(
          'font-semibold text-sm',
          isDisabled ? 'text-gray-400' : 'text-gray-900'
        )}
      >
        {planName}
      </span>

      {/* Price */}
      <div className="text-center mt-1">
        {/* Top line - fixed height */}
        <div className="h-4">
          {pricing.topLine && (
            <span
              className={cn(
                'text-[10px] text-gray-400',
                pricing.topLineStrikethrough && 'line-through'
              )}
            >
              {pricing.topLine}
            </span>
          )}
        </div>
        <div className="flex items-baseline justify-center gap-0.5">
          <span
            className={cn(
              'text-xl font-bold',
              isDisabled ? 'text-gray-400' : 'text-gray-900'
            )}
          >
            {pricing.main}
          </span>
          <span
            className={cn(
              'text-[10px]',
              isDisabled ? 'text-gray-400' : 'text-gray-500'
            )}
          >
            {pricing.sub}
          </span>
        </div>
      </div>

      {/* CTA Button */}
      <Button
        variant={planId === 'pro' && !isDisabled ? 'primary' : isCurrentPlan ? 'ghost' : 'secondary'}
        size="sm"
        className="w-full mt-2 text-xs"
        onClick={onSelect}
        disabled={isCurrentPlan || isDisabled || isFree || isLoading}
      >
        {isLoading ? '...' : getButtonText()}
      </Button>
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export function FeatureComparisonV2({
  currentPlanId,
  currentBucketMax = 0,
  isEligibleForIntro = true,
  onSelectPlan,
  isLoading = false,
  loadingPlanId,
  interval,
  onIntervalChange,
  sliderIndex,
  onSliderIndexChange,
  className,
}: FeatureComparisonV2Props) {
  const categories = getOrderedCategories();
  const featuresByCategory = getFeaturesByCategory();
  const plans = getOrderedPlanTiers();

  const currentStop = SLIDER_STOPS[sliderIndex];
  const selectedRequests = currentStop?.requests ?? 10000;

  const handlePlanSelect = (planId: PlanId) => {
    const bucket = getBucketForRequestCount(selectedRequests, planId);
    onSelectPlan?.(planId, bucket?.maxRequests ?? selectedRequests, interval);
  };

  // Determine if selecting a plan at the current slider position would be a downgrade
  const isDowngradeForPlan = (planId: PlanId): boolean => {
    if (!currentPlanId || currentPlanId === 'free' || currentBucketMax === 0) {
      return false;
    }
    const bucket = getBucketForRequestCount(selectedRequests, planId);
    if (!bucket) return false;
    // Use tolerance since bucket maxRequests (19999) vs metadata (20000) can differ slightly
    return bucket.maxRequests < (currentBucketMax - 1000);
  };

  const renderValue = (value: string | boolean | number, isDisabled: boolean) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className={cn('w-5 h-5 mx-auto', isDisabled ? 'text-gray-300' : 'text-success-500')} />
      ) : (
        <Minus className="w-5 h-5 text-gray-300 mx-auto" />
      );
    }

    if (typeof value === 'number') {
      if (value === -1)
        return (
          <span className={isDisabled ? 'text-gray-400' : 'text-gray-900'}>
            Unlimited
          </span>
        );
      return (
        <span className={isDisabled ? 'text-gray-400' : 'text-gray-900'}>
          {value.toLocaleString()}
        </span>
      );
    }

    return (
      <span className={isDisabled ? 'text-gray-400' : 'text-gray-900'}>
        {value}
      </span>
    );
  };

  // Calculate column widths - feature column + equal width for each plan
  const planCount = plans.length;

  return (
    <div id="feature-comparison" className={cn('w-full', className)}>
      {/* Non-sticky Header */}
      <div className="text-center py-6">
        <h2 className="text-2xl font-bold text-gray-900">Same API, more room to grow.</h2>
        <p className="text-gray-500 mt-2">
        Every plan uses the same endpoints and infrastructure. Higher tiers raise your limits and unlock advanced add-ons.
        </p>
      </div>

      {/* Non-sticky Confirmation Benefits */}
      <div className="flex flex-wrap justify-center gap-6 mb-6 text-sm text-gray-600">
        {CONFIRMATION_BENEFITS.map((benefit, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <benefit.icon className="w-4 h-4 text-brand-500" />
            <span>{benefit.text}</span>
          </div>
        ))}
      </div>

      {/* Sticky Controls + Table Header */}
      <div className="sticky -top-6 z-30 bg-gray-50 pb-0 -mx-6 px-6">
        {/* Controls Row: Slider on left, Toggle on right */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 p-6 bg-gray-50 rounded-t-xl border border-b-0 border-gray-200">
          {/* Slider Section */}
          <div className="flex-1 max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              How many requests do you need?
            </label>
            <div className="flex items-center gap-4">
              <span className="text-2xl font-bold text-gray-900 min-w-[80px]">
                {formatRequestCount(selectedRequests)}
              </span>
              <input
                type="range"
                min={0}
                max={SLIDER_STOPS.length - 1}
                value={sliderIndex}
                onChange={(e) => onSliderIndexChange(parseInt(e.target.value, 10))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${(sliderIndex / (SLIDER_STOPS.length - 1)) * 100}%, #e5e7eb ${(sliderIndex / (SLIDER_STOPS.length - 1)) * 100}%, #e5e7eb 100%)`,
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-gray-400">
              <span>1K</span>
              <span>5M+</span>
            </div>
          </div>

          {/* Billing Toggle */}
          <div className="flex items-center gap-2 p-1 bg-white rounded-lg border border-gray-200">
            <button
              onClick={() => onIntervalChange('monthly')}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                interval === 'monthly'
                  ? 'bg-brand-500 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              Pay monthly
            </button>
            <button
              onClick={() => onIntervalChange('annual')}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors relative',
                interval === 'annual'
                  ? 'bg-brand-500 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              Pay yearly
              <span
                className={cn(
                  'ml-2 text-xs px-1.5 py-0.5 rounded',
                  interval === 'annual'
                    ? 'bg-white/20 text-white'
                    : 'bg-success-100 text-success-700'
                )}
              >
                2 months free
              </span>
            </button>
          </div>
        </div>

        {/* Table Header with Plan Cards */}
        <div className="bg-white border-x border-gray-200 overflow-visible">
          <table className="w-full border-collapse table-fixed">
            <colgroup>
              <col style={{ width: '200px' }} />
              {plans.map((plan) => (
                <col key={plan.id} style={{ width: `calc((100% - 200px) / ${planCount})` }} />
              ))}
            </colgroup>
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-4 font-medium text-gray-500 bg-white align-bottom">
                  Feature
                </th>
                {plans.map((plan) => {
                  const isDisabled = isPlanDisabledAtRequestCount(plan.id, selectedRequests);
                  const bucket = getBucketForRequestCount(selectedRequests, plan.id);
                  const isDowngrade = isDowngradeForPlan(plan.id);

                  // "Current Plan" only if same plan AND same bucket
                  // Use tolerance for bucket comparison since metadata stores round numbers (20000)
                  // but actual bucket maxRequests might be 19999
                  const isSamePlan = currentPlanId === plan.id;
                  const isFreeWithNoBucket = plan.id === 'free' && currentBucketMax === 0;
                  const bucketMatchesTolerance = bucket && currentBucketMax > 0 &&
                    Math.abs(bucket.maxRequests - currentBucketMax) <= 1000;
                  // For paid plans, only mark as current if bucket actually matches
                  // For free plan, mark as current if user is on free (no bucket)
                  const isCurrentPlanAndBucket = isSamePlan && (bucketMatchesTolerance || isFreeWithNoBucket);

                  return (
                    <th
                      key={plan.id}
                      className={cn(
                        'text-center p-0 bg-white align-bottom',
                        currentPlanId === plan.id && 'bg-brand-50',
                        plan.id === 'pro' && !isDisabled && 'bg-brand-50'
                      )}
                    >
                      <PlanHeader
                        planId={plan.id}
                        planName={plan.name}
                        interval={interval}
                        selectedRequests={selectedRequests}
                        isCurrentPlan={isCurrentPlanAndBucket}
                        isDisabled={isDisabled}
                        isEligibleForIntro={isEligibleForIntro}
                        isDowngrade={isDowngrade}
                        onSelect={() => handlePlanSelect(plan.id)}
                        isLoading={isLoading && loadingPlanId === plan.id}
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
          </table>
        </div>
      </div>

      {/* Table Body */}
      <div className="bg-white border-x border-b border-gray-200 rounded-b-xl overflow-visible">
        <table className="w-full border-collapse table-fixed">
          <colgroup>
            <col style={{ width: '200px' }} />
            {plans.map((plan) => (
              <col key={plan.id} style={{ width: `calc((100% - 200px) / ${planCount})` }} />
            ))}
          </colgroup>
          <tbody>
            {categories.map((category) => (
              <Fragment key={category}>
                {/* Category header */}
                <tr className="bg-gray-50">
                  <td
                    colSpan={plans.length + 1}
                    className="py-3 px-4 font-semibold text-gray-700 text-sm uppercase tracking-wide"
                  >
                    {FEATURE_CATEGORY_LABELS[category]}
                  </td>
                </tr>

                {/* Features in category */}
                {featuresByCategory[category].map((feature) => (
                  <tr
                    key={feature.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-3 px-4 bg-white">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">{feature.name}</span>
                        <Tooltip content={feature.tooltip}>
                          <Info className="w-4 h-4 text-gray-400 cursor-help flex-shrink-0" />
                        </Tooltip>
                      </div>
                    </td>
                    {plans.map((plan) => {
                      const isDisabled = isPlanDisabledAtRequestCount(
                        plan.id,
                        selectedRequests
                      );
                      return (
                        <td
                          key={`${feature.id}-${plan.id}`}
                          className={cn(
                            'py-3 px-4 text-center text-sm bg-white',
                            isDisabled && 'opacity-50',
                            currentPlanId === plan.id && 'bg-brand-50/50',
                            plan.id === 'pro' && !isDisabled && 'bg-brand-50/50'
                          )}
                        >
                          {renderValue(feature.values[plan.id], isDisabled)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile hint */}
      <p className="text-xs text-gray-400 text-center mt-4 lg:hidden">
        Scroll horizontally to see all plans
      </p>
    </div>
  );
}
